import React, { useState } from 'react';
import { Button, CircularProgress, Typography, Box, TextField, FormControl, FormHelperText } from '@mui/material';
import { FaFileCsv } from 'react-icons/fa'; // CSV icon
import csv from 'csvtojson';
import { getNextCourseId, saveCourseDetails } from '../config/firestoreConfig';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { GoogleGenerativeAI } from '@google/generative-ai';


const JobPostAutomation = () => {
  const [loading, setLoading] = useState(false);
  const [responseData, setResponseData] = useState([]);
  const [error, setError] = useState(null);
  const [jobFile, setJobFile] = useState(null);
  const [questionFile, setQuestionFile] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Check if it's mobile
  const isTablet = useMediaQuery(theme.breakpoints.down('md')); // Check if it's tablet

  const readCSV = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const csvData = reader.result;
          const json = await csv().fromString(csvData);
          resolve(json);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  const generateJobAnswer = async (question, jobDescription) => {
    try {
    
      console.log('Google API Key:', process.env);

      const apiKey = "AIzaSyDfbcj8Hg7WGZdUw8iFezl6v20JKmnr9_k";
    // const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
      if (!apiKey) throw new Error('Missing API key.');

      const genAI = new GoogleGenerativeAI(apiKey);
      const model = await genAI.getGenerativeModel({ model: 'gemini-1.5-flash' });

      const prompt = `
        Based on the following job description, answer the question. If the answer is not explicitly mentioned in the job description, provide a common or typical response for someone in this role:

        ${jobDescription}

        ${question}
      `;

      const result = await model.generateContent(prompt);
      return result.response.text();
    } catch (error) {
      console.error('Error generating content:', error.message);
      return null;
    }
  };

  const processFiles = async () => {
    if (!jobFile || !questionFile) {
      setError('Please upload both the job descriptions and questions files.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const jobDescriptions = await readCSV(jobFile);
      const questions = await readCSV(questionFile);

      // Process each job description individually
      for (let job of jobDescriptions) {
        const jobDescription = job.description;
        const jobTitle = job.title;
        const jobApplyUrl = job.apply_url;

        // Initialize curriculum and learning objectives for each job
        const curriculumTitles = new Set();
        const learningObjectivesSet = new Set();
        const responses = [];

        for (let question of questions) {
          const questionText = question.questions;
          const questionTitle = question.title;
          const response = await generateJobAnswer(questionText, jobDescription);

          if (response) {
            // Store the response for this question and job
            responses.push({
              title: questionTitle,
              content: response,
            });

            // Add the question as a curriculum title
            curriculumTitles.add(questionTitle);

            // Create a learning objective based on the answer
            const objective = `Gain understanding of: ${response.slice(0, 80)}...`; // Summarize the response
            learningObjectivesSet.add(objective);
          }
        }

        // Get the next course ID for the new course
        const nextCourseId = await getNextCourseId();

        // Build course data for this particular job post
        const courseData = {
          title: jobTitle,
          description: jobDescription,
          category: "Job Decode",
          content: responses,
          learningObjectives: Array.from(learningObjectivesSet),
          targetAudience: "Students interested in this field",
          instructor: {
            name: "Valarchi",
            bio: "Social Influence",
          },
          curriculum: Array.from(curriculumTitles).map((title) => ({
            title: title,
          })),
          reviews: [],
          price: "No Cost",
          enrolled_persons: [],
          apply_url: jobApplyUrl,
          apply_url_clicked: [],
          createdAt: new Date().toISOString(), 
        };

        // Save course details to Firestore if course data is valid
        if (courseData) {
          await saveCourseDetails(nextCourseId, courseData);
          setResponseData([]);
        }
      }
    } catch (error) {
      setError('Error processing files. Please check the console for details.');
      console.error('Error processing files:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh', 
        flexDirection: 'column',
        padding: isMobile ? '16px' : '24px',
      }}
    >
      <Typography
        variant={isMobile ? 'h4' : 'h3'} 
        component="h1"
        sx={{
          fontWeight: 'bold',
          mb: isMobile ? 2 : 4,
          color: '#000',
          fontSize: isMobile ? '1.8rem' : '2.5rem',
          textAlign: 'center',
        }}
      >
        Job Post Automation
      </Typography>
  
      <Box sx={{ width: '100%', maxWidth: '600px' }}>
        <Box sx={{ marginBottom: isMobile ? '4px' : '8px' }}>
          <Typography variant="body1" sx={{ marginBottom: '2px' }}> 
            Upload Job Descriptions (.csv):
          </Typography>
          <FormControl fullWidth error={error && !jobFile}>
            <TextField
              type="file"
              accept=".csv"
              onChange={(e) => setJobFile(e.target.files[0])}
              sx={{ mb: 1 }} 
            />
            {error && !jobFile && (
              <FormHelperText>Job Descriptions file is required.</FormHelperText>
            )}
          </FormControl>
        </Box>
  
        <Box sx={{ marginBottom: isMobile ? '4px' : '8px' }}>
          <Typography variant="body1" sx={{ marginBottom: '2px' }}>
            Upload Questions (.csv):
          </Typography>
          <FormControl fullWidth error={error && !questionFile}>
            <TextField
              type="file"
              accept=".csv"
              onChange={(e) => setQuestionFile(e.target.files[0])}
              sx={{ mb: 1 }} 
            />
            {error && !questionFile && (
              <FormHelperText>Questions file is required.</FormHelperText>
            )}
          </FormControl>
        </Box>
  
        <Button
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 'bold',
            backgroundColor: '#000', 
            color: '#fff', 
            '&:hover': {
              backgroundColor: '#fff', 
              color: '#000', 
              border: '1px solid #000', 
            },
            display: 'flex',
            alignItems: 'center',
            gap: 1, 
            width: '100%',
            maxWidth: '250px',
            margin: '0 auto', 
            padding: isMobile ? '8px' : '10px', 
          }}
          onClick={processFiles}
          disabled={loading}
        >
          <Box sx={{ fontSize: isMobile ? 18 : 20 }}>
            <FaFileCsv />
          </Box>
          <Typography sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>
            Process Job Descriptions
          </Typography>
        </Button>
  
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, color: '#000' }}>
            <CircularProgress />
          </Box>
        )}
  
        {error && (
          <Typography color="error" sx={{ mt: 2, textAlign: 'center' }}>
            {error}
          </Typography>
        )}
  
        {responseData.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ textAlign: 'center', color: '#000' }}>
              Processed Responses:
            </Typography>
            <pre>{JSON.stringify(responseData, null, 2)}</pre>
          </Box>
        )}
      </Box>
    </Box>
  );
  
};

export default JobPostAutomation;
